import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import { ParameterSectionParameterType, ParameterSectionType } from 'common/components/parametersSections/models';
import i18n from 'common/services/I18n';
import Utils from 'common/services/Utils';
import { CSSProperties } from 'react';
import { findLineByKey } from 'store/budget/reducer';
import store from 'store/store';
import colors from 'styles/export/colors.module.scss';

export const BODY_CLICK_IGNORE_CLASS_NAME = 'BODY_CLICK_IGNORE';

const levelMultiplier = 10;
const defaultBorderRadius = 8;

export const lineLevelStyle = (level: number, clone: boolean, insideLevel: number): CSSProperties => {
    return {
        marginLeft: clone ? level : ((level - insideLevel) * levelMultiplier),
    };
}

export const lineInnerLevelStyle = (parentIsSelected: boolean, insideLevel: number): CSSProperties => {
    if (parentIsSelected) {
        return {
            paddingLeft: (insideLevel * levelMultiplier),
        };
    }
    return {};
}

export const lineOutterLineStyle = (isActive: boolean): CSSProperties => {
    return {
        marginTop: isActive ? 4 : undefined,
    };
}

export const lineActiveStyle = (isActive: boolean): CSSProperties => {
    return {
        borderWidth: isActive ? 0 : undefined,
    };
}

export const lineInnerActiveStyle = (isActive: boolean, parentIsSelected: boolean, isLast: boolean, hasChildren: boolean, isOpen: boolean, borderTopLeftWithoutRadius: boolean = true): CSSProperties => {
    const defaultBorderColor = isActive || parentIsSelected ? colors.primaryDark : 'transparent';

    let result: CSSProperties = {
        borderWidth: isActive || parentIsSelected ? 1 : 0,
        borderStyle: 'solid',
        borderTopColor: defaultBorderColor,
        borderBottomColor: defaultBorderColor,
        borderLeftColor: defaultBorderColor,
        borderRightColor: defaultBorderColor,
        borderTopRightRadius: defaultBorderRadius,
        borderTopLeftRadius: borderTopLeftWithoutRadius ? 0 : defaultBorderRadius,
        borderBottomLeftRadius: defaultBorderRadius,
        borderBottomRightRadius: defaultBorderRadius,
    }

    if (parentIsSelected) {
        result = {
            ...result,
            borderTopColor: 'transparent',
            borderBottomColor: isLast ? colors.primaryDark : 'transparent',
            borderBottomLeftRadius: !isLast ? 0 : defaultBorderRadius,
            borderBottomRightRadius: !isLast ? 0 : defaultBorderRadius,
            borderTopRightRadius: 0,
        };
    }

    if (hasChildren && isActive && isOpen) {
        result = {
            ...result,
            borderBottomColor: 'transparent',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        };
    }

    if (isActive) {
        result.paddingTop = 0;
    }

    return result;
}

export enum LinesTableColumn {
    DESIGNATION = 'DESIGNATION',
    ELEMENT_IMAGE = 'ELEMENT_IMAGE',
    QUANTITY = 'QUANTITY',
    UNIT = 'UNIT',
    GROSS_UNIT_PRICE = 'GROSS_UNIT_PRICE',
    UNIT_PRICE = 'UNIT_PRICE',
    UNIT_PRICE_WITH_CHILDREN = 'UNIT_PRICE_WITH_CHILDREN',
    TOTAL_EXCLUDING_TAX = 'TOTAL_EXCLUDING_TAX',
    TOTAL_INCLUDING_TAX = 'TOTAL_INCLUDING_TAX',
    TOTAL_UNIT_EXCLUDING_TAX = 'TOTAL_UNIT_EXCLUDING_TAX',
    TAX = 'TAX',
    MARGIN = 'MARGIN',
    DISCOUNT = 'DISCOUNT',
    LINE_NUMBER = 'LINE_NUMBER',
}

export function isColumnVisible(hiddenColumns: LinesTableColumn[] | null, field: LinesTableColumn) {
    return !(hiddenColumns ?? []).find(c => c === field)
}

export function getLevelFromParentKeys(parentKey: string, parentKeys: string[]): number {
    const index = parentKeys.indexOf(parentKey);
    if (index === -1) {
        throw new Error(`Key "${parentKey}" not found in the parentKeys.`);
    }
    return parentKeys.length - index;
}

export function isLastInLevel(allLines: BudgetLineDto[], parentKey: string, lineKey: string): boolean {
    if (allLines.length === 0) {
        return false;
    }

    function isLast(lines: BudgetLineDto[]): boolean {
        const lastLine = lines[lines.length - 1];
        if (!lastLine) {
            return false;
        }

        const lastLineChildren = allLines.filter(x => x.parentKey === lastLine.key)

        if ((lastLine.lineType !== BudgetLineType.SECTION || (lastLine.lineType === BudgetLineType.SECTION && lastLine.isOpen)) && lastLineChildren && lastLineChildren.length > 0) {
            return isLast(lastLineChildren);
        }

        if (lastLine.key === lineKey) {
            return true;
        }

        return false;
    }

    const parentChildren = allLines.filter(x => x.parentKey === parentKey)
    return isLast(parentChildren);
}

export function isLineBeingCut(allLines: BudgetLineDto[], lineKey: string, lineBeingCutKey: string): boolean {
    if (allLines.length === 0) {
        return false;
    }

    const isCutOrParentCut = (lineKey: string): boolean => {
        if (lineKey === lineBeingCutKey) {
            return true;
        }

        const line = findLineByKey(lineKey, allLines);
        if (line && line.parentKey) {
            return isCutOrParentCut(line.parentKey);
        }

        return false;
    };

    return isCutOrParentCut(lineKey);
}

export const mergeWithExistingParameters = (newParameters: BudgetLineParameterDto[], existingParameters: BudgetLineParameterDto[]): BudgetLineParameterDto[] => {
    if (!existingParameters || existingParameters.length === 0) {
        return newParameters
    }

    const existingParametersMap = new Map<string, BudgetLineParameterDto>();
    for (const param of existingParameters) {
        if (param.elementParameterId) {
            existingParametersMap.set(param.elementParameterId, param);
        }
    }

    const mergedParameters = newParameters.map(newParam => {
        const existingParam = newParam.elementParameterId ? existingParametersMap.get(newParam.elementParameterId) : null;
        if (existingParam) {
            return {
                ...existingParam,
                children: mergeWithExistingParameters(newParam.children || [], existingParam.children || [])
            };
        }
        return {
            ...newParam,
            children: newParam.children ? mergeWithExistingParameters(newParam.children, []) : []
        };
    });

    const existingOnlyParameters = existingParameters.filter(existingParam => !newParameters.find(newParam => newParam.elementParameterId === existingParam.elementParameterId));

    const result = [...mergedParameters, ...existingOnlyParameters];

    result.sort((a, b) => a.position - b.position);

    return result;
}

export const getExpressionParameters = (currentLineKey: string): ExpressionParameter[] => {
    const t = i18n.t;
    const result: ExpressionParameter[] = [];
    const budget = store.getState().budget.budget;
    const lines = store.getState().budget.lines;

    const currentLine = findLineByKey(currentLineKey, lines);
    const currentParentLineKey = currentLine && currentLine.parentKey ? currentLine.parentKey : null;

    function createForParameters(parameters: BudgetLineParameterDto[], idsPrefix: string[], namesPrefix: string[]) {
        for (const parameter of parameters) {
            if (parameter.type === ParameterSectionType.PARAMETER) {
                switch (parameter.parameterType) {
                    case ParameterSectionParameterType.PRODUCTS:
                    case ParameterSectionParameterType.IMAGES:
                    case ParameterSectionParameterType.TEXT:
                        break;

                    case ParameterSectionParameterType.NUMBER_MIN_MAX:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.minMax?.value ?? 0,
                        });
                        break;

                    case ParameterSectionParameterType.NUMBER:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.number?.number ?? 0,
                        });
                        break;

                    case ParameterSectionParameterType.QUANTITY:
                        result.push({
                            id: ExpressionSystem.formatParameterId([...idsPrefix, parameter.familyParameterId ?? '', 'parameter']),
                            name: ExpressionSystem.formatParameterName([...namesPrefix, parameter.parameterName ?? '']),
                            value: parameter.quantity?.quantity ?? 0,
                        });
                        break;

                    default:
                        break;
                }
            } else {
                createForParameters(parameter.children ?? [], [...idsPrefix], [...namesPrefix, parameter.title ?? '']);
            }
        }
    }

    function createForBudget() {
        const budgetName = t('budgets.budget.budget');
        const budgetKey = 'budget';

        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalNetExcludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_net_without_tax')]),
            value: budget.totalNetExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'discount']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.discount')]),
            value: budget.discount,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalExcludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_without_tax')]),
            value: budget.totalExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_tax')]),
            value: budget.totalTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([budgetKey, 'totalIncludingTax']),
            name: ExpressionSystem.formatParameterName([budgetName, t('budgets.budget.lines.totals.total_with_tax')]),
            value: budget.totalIncludingTax,
        });
    }

    function createForLine(line: BudgetLineDto, idsPrefix: string[], namesPrefix: string[]) {
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'quantity']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.quantity')]),
            value: line.quantity,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'grossUnitPrice']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.gross_unit_price')]),
            value: line.grossUnitPrice,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'marginPercentage']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.margin')]),
            value: line.marginPercentage,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'discountPercentage']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.discount')]),
            value: line.discountPercentage,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'unitPrice']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.price_unity')]),
            value: line.unitPrice,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'totalUnitExcludingTax']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.total_unit_without_tax')]),
            value: line.totalUnitExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'unitPriceWithChildren']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.unit_price_with_children')]),
            value: line.unitPriceWithChildren,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'totalExcludingTax']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.total_without_tax')]),
            value: line.totalExcludingTax,
        });
        result.push({
            id: ExpressionSystem.formatParameterId([...idsPrefix, 'totalIncludingTax']),
            name: ExpressionSystem.formatParameterName([...namesPrefix, t('budgets.budget.lines.header.total_with_tax')]),
            value: line.totalIncludingTax,
        });

        if (line.parameters && line.parameters.length > 0) {
            createForParameters(line.parameters, [...idsPrefix], [...namesPrefix]);
        }
    }

    createForBudget();

    for (const line of lines) {
        let lineName = `${line.designation ? line.designation : (`(${t('budgets.budget.lines.without_designation')})`)} (${line.number})`;
        let lineKey = line.key;

        if (line.key === currentLineKey) {
            lineName = `${t('budgets.budget.lines.current_line')}`;
            lineKey = 'CURRENT_LINE';
        } else if (currentParentLineKey && line.key === currentParentLineKey) {
            lineName = `${t('budgets.budget.lines.current_parent_line')}`;
            lineKey = 'CURRENT_PARENT_LINE';
        }

        createForLine(line, [lineKey], [lineName]);
    }

    return result;
}

export function mapNewParametersToExisting(parameters: BudgetLineParameterDto[], existingParameters: BudgetLineParameterDto[]): BudgetLineParameterDto[] {
    return parameters.map(p => {
        const existingParameter = existingParameters.find(ep => ep.familyParameterId === p.familyParameterId);
        if (existingParameter) {
            return {
                ...existingParameter,
                id: undefined,
                key: Utils.newGuid(),
                children: mapNewParametersToExisting(p.children ?? [], existingParameter.children ?? []),
                elements: p.elements?.map(product => ({
                    ...product,
                    parameters: mapNewParametersToExisting(product.parameters ?? [], existingParameter.elements?.find(ep => ep.elementId === product.elementId)?.parameters ?? []),
                })) ?? [],
                minMax: p.minMax
                    ? {
                        ...p.minMax,
                        elements: p.minMax.elements?.map(product => ({
                            ...product,
                            parameters: mapNewParametersToExisting(product.parameters ?? [], existingParameter.minMax?.elements?.find(ep => ep.elementId === product.elementId)?.parameters ?? []),
                        })) ?? [],
                    }
                    : p.minMax,
            };
        } else {
            return {
                ...p,
                id: undefined,
                key: Utils.newGuid(),
                children: mapNewParametersToExisting(p.children ?? [], []),
                elements: p.elements?.map(product => ({
                    ...product,
                    parameters: mapNewParametersToExisting(product.parameters ?? [], []),
                })) ?? [],
                minMax: p.minMax
                    ? {
                        ...p.minMax,
                        elements: p.minMax.elements?.map(product => ({
                            ...product,
                            parameters: mapNewParametersToExisting(product.parameters ?? [], []),
                        })) ?? [],
                    }
                    : p.minMax,
            };
        }
    });
}
